import cn from 'classnames';
import { isString } from 'lodash';
import {
  type MemoExoticComponent,
  memo,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { Location, To } from 'react-router-dom';

import { removeButtonAppearance } from '../../../../utils/style';
import { MenuItemButton } from '../../../navigation/menu/item/MenuItemButton';
import { Menu } from '../../../navigation/menu/Menu';
import * as styles from './Sidebar.styles';

export type SidebarProps = {
  isOpen?: boolean;
} & PropsWithChildren;

export type MenuItemShape = {
  name: string;
  label: ReactNode;
  route: To;
  icon?: ReactElement;
  isActiveFn?: (route: To) => boolean;
  hasActivity?: boolean;
};

type SidebarMenuItemProps = {
  item: MenuItemShape;
  location: Location;
  onClick?: (name: string) => void;
};

const SidebarBase = ({ children, isOpen = false }: SidebarProps) => {
  return (
    <div
      css={styles.sidebarContainer}
      className={cn({
        isOpen,
      })}
    >
      {children}
    </div>
  );
};

const SidebarSection = ({ children }: PropsWithChildren) => {
  return children;
};

const SidebarItem = ({ children }: PropsWithChildren) => {
  return children;
};

const SidebarMenuItemLink = ({ item, location, onClick }: SidebarMenuItemProps) => (
  <button
    key={item.name}
    id={`sidebar-item-${item.name}`}
    type="button"
    css={removeButtonAppearance}
    onClick={() => onClick?.(item.name)}
  >
    <Menu.ItemLink
      label={item.label}
      icons={item.icon}
      href={item.route}
      isSelected={
        item.isActiveFn
          ? item.isActiveFn(item.route)
          : location.pathname.startsWith(isString(item.route) ? item.route : item.route.pathname)
      }
      hasActivity={item.hasActivity}
    />
  </button>
);

type CompoundedComponent = MemoExoticComponent<typeof SidebarBase> & {
  Section: typeof SidebarSection;
  Item: typeof SidebarItem;
  Menu: typeof Menu;
  MenuItemLink: typeof SidebarMenuItemLink;
  MenuItemButton: typeof MenuItemButton;
};

const Sidebar = memo(SidebarBase) as CompoundedComponent;

Sidebar.Section = memo(SidebarSection);
Sidebar.Item = memo(SidebarItem);
Sidebar.Menu = Menu;
Sidebar.MenuItemLink = SidebarMenuItemLink;
Sidebar.MenuItemButton = MenuItemButton;

Sidebar.displayName = 'Sidebar';

export { Sidebar };
